import {FeatureName} from '@hproduce/suite-core'
import React from 'react'
import {RouteObject, useRoutes} from 'react-router'

/**
 * Creates a lazy-loaded component that renders the feature's RouteObject via useRoutes().
 * Each call produces a separate webpack chunk for the feature.
 */
function createLazyFeature(
  loader: () => Promise<RouteObject>
): React.LazyExoticComponent<React.ComponentType> {
  return React.lazy(async () => {
    const route = await loader()
    function FeatureContent() {
      return useRoutes([route])
    }
    return {default: FeatureContent}
  })
}

export const featureComponentMap: Record<
  FeatureName,
  React.LazyExoticComponent<React.ComponentType> | undefined
> = {
  HrocChecklistTemplates: createLazyFeature(() =>
    import('@features/hroc-checklists').then((m) => m.hrocChecklistTemplatesRoute)
  ),
  DailyReportingJanus: createLazyFeature(() =>
    import('@hconnect/janus/src/routes/dailyEntryRoutes').then((m) => m.dailyEntryRoutes)
  ),
  MonthlyReportingJanus: createLazyFeature(() =>
    import('@hconnect/janus/src/routes/monthlyEntryRoutes').then((m) => m.monthlyEntryRoutes)
  ),
  PssStandardCapacities: createLazyFeature(() =>
    import('@hconnect/janus/src/routes/pssStandardCapacitiesRoutes').then(
      (m) => m.pssStandardCapacitiesRoutes
    )
  ),
  InventoryOpeningBalances: createLazyFeature(() =>
    import('@hconnect/janus/src/routes/inventoryOpeningBalancesRoutes').then(
      (m) => m.inventoryOpeningBalancesRoutes
    )
  ),
  PlantAdministration: createLazyFeature(() =>
    import('@hconnect/janus/src/routes/plantAdministrationRoutes').then(
      (m) => m.plantAdministrationRoutes
    )
  ),
  PSSJanus: createLazyFeature(() =>
    import('@hconnect/janus/src/routes/pssRoutes').then((m) => m.pssRoutes)
  ),
  Validation: createLazyFeature(() =>
    import('@hconnect/janus/src/routes/validationRoutes').then((m) => m.validationRoutes)
  ),
  ValidationAndChecks: createLazyFeature(() =>
    import('@hconnect/janus/src/routes/validationAndChecksRoutes').then(
      (m) => m.validationAndChecksRoutes
    )
  ),
  InputRangesAndDecimals: createLazyFeature(() =>
    import('@hconnect/janus/src/routes/inputRangesAndDecimalsRoutes').then(
      (m) => m.inputRangesAndDecimalsRoutes
    )
  ),
  VersionManagement: createLazyFeature(() =>
    import('@hconnect/janus/src/routes/versionManagementRoutes').then(
      (m) => m.versionManagementRoutes
    )
  ),
  MajorOutageLeadDashboard: createLazyFeature(() =>
    import('@features/major-outage-lead-dashboard').then((m) => m.majorOutageLeadDashboardRoute)
  ),
  PerformanceReview: createLazyFeature(() =>
    import('@features/performance-review').then((m) => m.performanceReviewRoutes)
  ),
  ProcessEngineerDashboard: createLazyFeature(() =>
    import('@features/process-engineer-dashboard').then((m) => m.processEngineerDashboardRoute)
  ),
  ReliabilityEngineerDashboard: createLazyFeature(() =>
    import('@features/reliability-engineer-dashboard').then(
      (m) => m.reliabilityEngineerDashboardRoute
    )
  ),
  OptimizationSpecialistDashboard: createLazyFeature(() =>
    import('@features/optimization-specialist-dashboard').then(
      (m) => m.optimizationSpecialistDashboardRoute
    )
  ),
  JobsToBeDone: createLazyFeature(() =>
    import('@features/jobs-to-be-done').then((m) => m.jobsToBeDoneDashboardRoute)
  ),
  RcfaCompliance: createLazyFeature(() =>
    import('@features/rcfa-compliance').then((m) => m.rcfaComplianceRoute)
  ),
  SlaTracking: createLazyFeature(() =>
    import('@features/sla-tracking').then((m) => m.slaTrackingRoutes)
  ),
  SystemsEngineerDashboard: createLazyFeature(() =>
    import('@features/systems-engineer-dashboard').then((m) => m.systemsEngineerDashboardRoutes)
  ),
  RcfaList: createLazyFeature(() => import('@features/rcfa-list').then((m) => m.rcfaListRoutes)),
  FuelMixOptimizer: createLazyFeature(() =>
    import('@features/fuel-mix-optimizer').then((m) => m.fuelMixOptimizerRoutes)
  ),
  StoppageList: createLazyFeature(() =>
    import('@features/stoppage-list').then((m) => m.stoppageListRoutes)
  ),
  ShiftEvents: createLazyFeature(() =>
    import('@features/shift-events').then((m) => m.shiftEventsRoutes)
  ),
  MaintainDashboard: createLazyFeature(() =>
    import('@features/maintain-dashboard').then((m) => m.maintainDashboardRoutes)
  ),
  ShiftSummary: createLazyFeature(() =>
    import('@features/shift-summary').then((m) => m.shiftSummaryRoutes)
  ),
  PlantActionList: createLazyFeature(() =>
    import('@features/pom').then((m) => m.easyPomPlantActionListRoutes)
  ),
  EasyPomReferencesLinks: createLazyFeature(() =>
    import('@features/pom').then((m) => m.easyPomReferenceLinksRoutes)
  ),
  EasyPomDashboard: createLazyFeature(() =>
    import('@features/pom').then((m) => m.easyPomDashboardRoutes)
  ),
  EasyPomComparativeAnalysis: createLazyFeature(() =>
    import('@features/pom').then((m) => m.easyPomComparativeAnalysisRoutes)
  ),
  DowntimeDashboard: createLazyFeature(() =>
    import('@features/downtime-dashboard').then((m) => m.downtimeDashboardRoutes)
  ),
  StoppageAnalysis: createLazyFeature(() =>
    import('@features/stoppage-analysis').then((m) => m.stoppageAnalysisRoutes)
  ),
  PerformanceReports: createLazyFeature(() =>
    import('@features/performance-reports').then((m) => m.performanceReportsRoutes)
  ),
  ExpertSystemUsageRate: createLazyFeature(() =>
    import('@features/expert-system-usage-rate').then((m) => m.expertSystemUsageRateRoute)
  ),
  ElectricityPriceForecast: createLazyFeature(() =>
    import('@features/electricity-price-forecast').then((m) => m.ElectricityPriceForecastRoute)
  ),
  SalesForecast: createLazyFeature(() =>
    import('@features/sales-forecast').then((m) => m.salesForecastRoutes)
  ),
  OperatorView: createLazyFeature(() =>
    import('@features/operator-view').then((m) => m.operatorViewRoutes)
  ),
  Tasks: createLazyFeature(() => import('@features/tasks').then((m) => m.tasksRoutes)),
  StorageLevels: createLazyFeature(() =>
    import('@features/storage-levels').then((m) => m.storageLevelsRoutes)
  ),
  ProductionPlanning: createLazyFeature(() =>
    import('@features/production-planning').then((m) => m.productionPlanningRoutes)
  ),
  ReportingReminders: createLazyFeature(() =>
    import('@hconnect/janus/src/routes/reportingRemindersRoutes').then(
      (m) => m.reportingRemindersRoutes
    )
  ),
  Optimizer: createLazyFeature(() => import('@features/optimizer').then((m) => m.optimizerRoutes)),
  RefractoryMaterials: createLazyFeature(() =>
    import('@features/refractory-materials').then((m) => m.refractoryMaterialsRoute)
  ),
  PerformanceData: createLazyFeature(() =>
    import('@features/performance-data').then((m) => m.performanceDataRoutes)
  ),
  InstructionsAndDocuments: createLazyFeature(() =>
    import('@features/instructions-and-documents').then((m) => m.instructionsAndDocumentsRoutes)
  ),
  LabQualityResult: createLazyFeature(() =>
    import('@features/lab-quality-result').then((m) => m.labQualityResultRoutes)
  )
}
